// SectionAccordion.js - top-level, user-renameable, reorderable quotation section.
// The "Priced" / "Informational" toggle controls both which item table renders
// (full cost fields vs. Section-C-style notes-only) and whether the section's total
// is included in the totals sidebar - see CreateInteriorQuatation.js.
import { ArrowDown, ArrowUp, ChevronDown, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

const iconBtnClass =
  'inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-colors hover:border-teal-300 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-40';

const SectionAccordion = ({
  section,
  totalLabel,
  onToggle,
  onRename,
  onTogglePriced,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onRemove,
  canRemove,
  children,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div
      className={`overflow-hidden rounded-2xl border shadow-sm ${
        section.isPriced ? 'border-gray-200 bg-white' : 'border-amber-200 bg-white'
      }`}
    >
      <div
        className={`flex flex-wrap items-center gap-2.5 px-4 py-3 ${
          section.isPriced ? 'bg-gray-50/80' : 'bg-amber-50/50'
        }`}
      >
        <button
          type="button"
          onClick={onToggle}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200/60"
          title={section.collapsed ? 'Expand section' : 'Collapse section'}
        >
          <ChevronDown className={`h-5 w-5 transition-transform ${section.collapsed ? '-rotate-90' : ''}`} />
        </button>
        <input
          type="text"
          value={section.name}
          onChange={(e) => onRename(e.target.value)}
          placeholder="Section name"
          className="h-9 min-w-[160px] flex-1 rounded-md border border-transparent bg-transparent px-2 text-base font-bold text-gray-900 hover:border-gray-200 focus:border-teal-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30"
        />
        <button
          type="button"
          role="switch"
          aria-checked={section.isPriced}
          onClick={onTogglePriced}
          title={
            section.isPriced
              ? 'Priced - included in totals. Click to make informational (excluded from totals).'
              : 'Informational - excluded from totals. Click to make priced (included in totals).'
          }
          className="inline-flex items-center gap-2 rounded-full px-1"
        >
          <span
            className={`text-xs font-semibold transition-colors ${
              !section.isPriced ? 'text-amber-800' : 'text-gray-400'
            }`}
          >
            Info
          </span>
          <span
            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
              section.isPriced ? 'bg-teal-600' : 'bg-amber-400'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                section.isPriced ? 'translate-x-[18px]' : 'translate-x-1'
              }`}
            />
          </span>
          <span
            className={`text-xs font-semibold transition-colors ${
              section.isPriced ? 'text-teal-800' : 'text-gray-400'
            }`}
          >
            Priced
          </span>
        </button>
        {totalLabel && (
          <span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700">{totalLabel}</span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={onMoveUp} disabled={!canMoveUp} title="Move section up" className={iconBtnClass}>
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={!canMoveDown}
            title="Move section down"
            className={iconBtnClass}
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={!canRemove}
            title="Remove section"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-red-500 hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {!section.collapsed && <div className="space-y-3 p-4">{children}</div>}

      <ConfirmDialog
        isOpen={showConfirm}
        title="Remove section?"
        message={`This will permanently remove "${section.name || 'this section'}" and all of its rooms and rows. This cannot be undone.`}
        confirmLabel="Remove"
        onCancel={() => setShowConfirm(false)}
        onConfirm={() => {
          setShowConfirm(false);
          onRemove?.();
        }}
      />
    </div>
  );
};

export default SectionAccordion;
